import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Slider,
  Grid,
  TextField,
  Button,
  Chip,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider,
  useTheme,
  alpha,
  Autocomplete,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  DirectionsCar,
  Brightness4,
  Brightness7,
  CalendarMonth,
  Route as RouteIcon,
  AccessTime,
  Search as SearchIcon,
} from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../services/api';

interface PlaybackPoint {
  timestamp: string;
  latitude: number;
  longitude: number;
  speed: number;
  heading: number;
  ignition: boolean;
  odometer: number;
  satellites?: number;
  altitude?: number;
  movement?: boolean;
}

interface TripStats {
  duration: number;
  distance: number;
  maxSpeed: number;
  avgSpeed: number;
  pointCount: number;
}

interface PlaybackData {
  device: {
    imei: string;
    plateNumber: string;
    make: string;
    modelYear: string;
  };
  timeRange: {
    start: string;
    end: string;
  };
  points: PlaybackPoint[];
  stats: TripStats;
}

// ✅ Simplified Vehicle interface for autocomplete
interface Vehicle {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  year: number;
  vin?: string;
  imei?: string; // ✅ IMEI directly on vehicle object
  deviceBinding?: {
    currentDeviceId: string;
    isDeviceBound?: boolean;
  };
}

const TripPlaybackPage: React.FC = () => {
  const theme = useTheme();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const carMarkerRef = useRef<mapboxgl.Marker | null>(null);

  // ✅ NEW: Vehicle search state
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  const [imei, setImei] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [playbackData, setPlaybackData] = useState<PlaybackData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [mapStyle, setMapStyle] = useState<'light' | 'dark'>('dark');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(10);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchVehicles = async () => {
    console.log('==========================================');
    console.log('🚀 fetchVehicles CALLED');
    console.log('==========================================');
    
    try {
      console.log('Step 1: Setting loading state...');
      setVehiclesLoading(true);
      
      console.log('Step 2: Calling API /vehicles with limit=1000...');
      const vehiclesResponse = await api.get('/vehicles', {
        params: { limit: 1000 }
      });
      
      console.log('✅ API Response received!');
      console.log('📦 Full response:', vehiclesResponse);
      console.log('📊 Response.data:', vehiclesResponse.data);
      console.log('📊 Response.data.data:', vehiclesResponse.data.data);
      console.log('📊 Total vehicles:', vehiclesResponse.data.data?.length || 0);
      
      if (!vehiclesResponse.data.data || vehiclesResponse.data.data.length === 0) {
        console.error('❌ No vehicles returned from API!');
        setError('No vehicles found in your account.');
        setVehicles([]);
        return;
      }
      
      console.log('Step 3: Filtering vehicles with bound devices...');
      const vehiclesWithDevices = vehiclesResponse.data.data.filter(
        (v: any) => {
          const hasDevice = v.deviceBinding?.currentDeviceId && v.deviceBinding?.isDeviceBound === true;
          console.log(`  🔍 ${v.plateNumber}: deviceId=${v.deviceBinding?.currentDeviceId}, isBound=${v.deviceBinding?.isDeviceBound}, hasDevice=${hasDevice}`);
          return hasDevice;
        }
      );
      
      console.log(`✅ Found ${vehiclesWithDevices.length} vehicles with devices`);
      console.log('📋 Vehicles with devices:', vehiclesWithDevices);
      
      if (vehiclesWithDevices.length === 0) {
        console.warn('⚠️ No vehicles have bound devices!');
        setError('No vehicles with devices found. Please bind devices to vehicles first.');
        setVehicles([]);
        return;
      }
      
      console.log('Step 4: Fetching IMEI for each device...');
      const vehiclesWithIMEI = await Promise.all(
        vehiclesWithDevices.map(async (vehicle: any, index: number) => {
          try {
            const deviceId = vehicle.deviceBinding?.currentDeviceId;
            console.log(`  [${index + 1}/${vehiclesWithDevices.length}] Fetching device ${deviceId} for ${vehicle.plateNumber}...`);
            
            if (!deviceId) {
              console.error(`    ❌ No device ID for ${vehicle.plateNumber}`);
              return null;
            }
            
            const deviceResponse = await api.get(`/devices/${deviceId}`);
            console.log(`    ✅ Device response:`, deviceResponse.data);
            
            const imei = deviceResponse.data.data.imei;
            console.log(`    ✅ IMEI: ${imei}`);
            
            const result = {
              ...vehicle,
              imei: imei
            };
            console.log(`    ✅ Result:`, result);
            
            return result;
          } catch (error: any) {
            console.error(`    ❌ Failed to fetch device for ${vehicle.plateNumber}:`, error);
            console.error(`    ❌ Error response:`, error.response);
            return null;
          }
        })
      );
      
      console.log('Step 5: Filtering out null results...');
      console.log('📋 All results (before filter):', vehiclesWithIMEI);
      
      const validVehicles = vehiclesWithIMEI.filter((v): v is Vehicle & { imei: string } => {
        const isValid = v !== null && v.imei !== undefined;
        console.log(`  Filter check: ${v?.plateNumber || 'null'} - isValid: ${isValid}`);
        return isValid;
      });
      
      console.log(`✅ Successfully loaded ${validVehicles.length} vehicles with IMEI`);
      console.log('📋 Final valid vehicles:', validVehicles);
      
      console.log('Step 6: Setting vehicles state...');
      setVehicles(validVehicles);
      console.log('✅ State updated!');
      
    } catch (error: any) {
      console.error('==========================================');
      console.error('❌ ERROR in fetchVehicles:');
      console.error('==========================================');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      setError(`Failed to load vehicles: ${error.response?.data?.message || error.message}`);
    } finally {
      console.log('Step 7: Setting loading to false...');
      setVehiclesLoading(false);
      console.log('==========================================');
      console.log('🏁 fetchVehicles COMPLETED');
      console.log('==========================================');
    }
  };

  // ✅ Fetch all vehicles on mount
  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    const now = new Date();
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    setEndTime(now.toISOString().slice(0, 16));
    setStartTime(twoWeeksAgo.toISOString().slice(0, 16));
  }, []);

  const getMapStyle = () => {
    return mapStyle === 'dark' 
      ? 'mapbox://styles/mapbox/navigation-night-v1'
      : 'mapbox://styles/mapbox/streets-v12';
  };

  // Initialize Mapbox
  useEffect(() => {
    const token = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic29waGllMDEiLCJhIjoiY21jbmZtaHBkMDNhMjJuczMxd3dmeWVobCJ9.7W1F9G-KWZRqsiNPEle4sA';
    mapboxgl.accessToken = token;

    if (mapRef.current || !mapContainerRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: getMapStyle(),
      center: [3.3792, 6.5244],
      zoom: 12,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    mapRef.current.on('load', () => {
      console.log('✅ Map loaded');
      
      // Add empty sources
      mapRef.current!.addSource('route-line', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      });

      mapRef.current!.addSource('trail-line', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      });

      // Full route (gray dashed)
      mapRef.current!.addLayer({
        id: 'route-background',
        type: 'line',
        source: 'route-line',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#888888',
          'line-width': 4,
          'line-opacity': 0.3,
          'line-dasharray': [3, 3]
        }
      });

      // Trail glow
      mapRef.current!.addLayer({
        id: 'trail-glow',
        type: 'line',
        source: 'trail-line',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#00ff00',
          'line-width': 16,
          'line-opacity': 0.6,
          'line-blur': 8
        }
      });

      // Trail solid line
      mapRef.current!.addLayer({
        id: 'trail-solid',
        type: 'line',
        source: 'trail-line',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#00ff00',
          'line-width': 8,
          'line-opacity': 1.0
        }
      });

      console.log('✅ Layers added');
    });

    return () => {
      carMarkerRef.current?.remove();
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  const toggleMapStyle = () => {
    const newStyle = mapStyle === 'dark' ? 'light' : 'dark';
    setMapStyle(newStyle);
    
    if (mapRef.current) {
      mapRef.current.setStyle(
        newStyle === 'dark' 
          ? 'mapbox://styles/mapbox/navigation-night-v1'
          : 'mapbox://styles/mapbox/streets-v12'
      );

      // Re-add sources and layers after style change
      mapRef.current.once('styledata', () => {
        setTimeout(() => {
          if (!mapRef.current) return;

          mapRef.current.addSource('route-line', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: playbackData ? playbackData.points.map(p => [p.longitude, p.latitude]) : []
              }
            }
          });

          mapRef.current.addSource('trail-line', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: playbackData ? playbackData.points.slice(0, currentIndex + 1).map(p => [p.longitude, p.latitude]) : []
              }
            }
          });

          mapRef.current.addLayer({
            id: 'route-background',
            type: 'line',
            source: 'route-line',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#888888', 'line-width': 4, 'line-opacity': 0.3, 'line-dasharray': [3, 3] }
          });

          mapRef.current.addLayer({
            id: 'trail-glow',
            type: 'line',
            source: 'trail-line',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#00ff00', 'line-width': 16, 'line-opacity': 0.6, 'line-blur': 8 }
          });

          mapRef.current.addLayer({
            id: 'trail-solid',
            type: 'line',
            source: 'trail-line',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#00ff00', 'line-width': 8, 'line-opacity': 1.0 }
          });
        }, 500);
      });
    }
  };

  const createCarMarkerElement = useCallback((speed: number, heading: number, ignition: boolean) => {
    const el = document.createElement('div');
    el.style.width = '70px';
    el.style.height = '70px';
    el.style.cursor = 'pointer';

    const speedColor = speed > 80 ? '#ff1744' : speed > 60 ? '#ff9800' : speed > 30 ? '#fbc02d' : '#00e676';
    
    el.innerHTML = `
      <svg width="70" height="70" viewBox="0 0 70 70" style="transform: rotate(${heading}deg); filter: drop-shadow(0 8px 16px rgba(0,0,0,0.6));">
        <g transform="translate(35,35)">
          <circle cx="0" cy="0" r="28" fill="${speedColor}" opacity="0.15"/>
          <circle cx="0" cy="0" r="24" fill="${speedColor}" opacity="0.25"/>
          <ellipse cx="2" cy="3" rx="20" ry="12" fill="rgba(0,0,0,0.4)"/>
          <defs>
            <linearGradient id="carBody${heading}" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${speedColor};stop-opacity:1" />
              <stop offset="50%" style="stop-color:${speedColor};stop-opacity:0.9" />
              <stop offset="100%" style="stop-color:${speedColor};stop-opacity:0.7" />
            </linearGradient>
            <linearGradient id="carRoof${heading}" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style="stop-color:#b3e5fc;stop-opacity:0.9" />
              <stop offset="100%" style="stop-color:#4fc3f7;stop-opacity:0.95" />
            </linearGradient>
          </defs>
          <path d="M-18,-9 L-18,-13 L-12,-17 L12,-17 L18,-13 L18,-9 L18,10 L12,13 L-12,13 L-18,10 Z" 
                fill="url(#carBody${heading})" stroke="white" stroke-width="2.5"/>
          <path d="M-12,-14 L-8,-17 L8,-17 L12,-14 L12,-8 L-12,-8 Z" 
                fill="url(#carRoof${heading})" stroke="white" stroke-width="2"/>
          <ellipse cx="-16" cy="-6" rx="3" ry="2.5" fill="${speedColor}" stroke="white" stroke-width="1.5"/>
          <ellipse cx="16" cy="-6" rx="3" ry="2.5" fill="${speedColor}" stroke="white" stroke-width="1.5"/>
          <circle cx="-14" cy="10" r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          <circle cx="14" cy="10" r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          <circle cx="-14" cy="-10" r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          <circle cx="14" cy="-10" r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          <ellipse cx="-10" cy="-16" rx="2.5" ry="2" fill="#ffffff" opacity="0.9"/>
          <ellipse cx="10" cy="-16" rx="2.5" ry="2" fill="#ffffff" opacity="0.9"/>
          <ellipse cx="-10" cy="12" rx="2" ry="1.5" fill="#ff0000" opacity="0.8"/>
          <ellipse cx="10" cy="12" rx="2" ry="1.5" fill="#ff0000" opacity="0.8"/>
          ${ignition ? `
            <circle cx="0" cy="-14" r="4" fill="#00e676" stroke="white" stroke-width="2" opacity="0.9">
              <animate attributeName="opacity" values="0.9;0.5;0.9" dur="2s" repeatCount="indefinite"/>
            </circle>
          ` : ''}
        </g>
      </svg>
    `;
    
    return el;
  }, []);

  const loadPlaybackData = async () => {
    if (!imei || !startTime || !endTime) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.get(`/telemetry/playback/${imei}`, {
        params: {
          startTime: new Date(startTime).toISOString(),
          endTime: new Date(endTime).toISOString(),
        },
      });

      const data = response.data.data;
      
      // CRITICAL FIX: Ensure points are sorted chronologically
      if (data.points && data.points.length > 0) {
        data.points.sort((a: PlaybackPoint, b: PlaybackPoint) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        
        console.log('✅ Sorted', data.points.length, 'points chronologically');
        console.log('🕐 First point:', new Date(data.points[0].timestamp).toLocaleString());
        console.log('🕐 Last point:', new Date(data.points[data.points.length - 1].timestamp).toLocaleString());
      }
      
      setPlaybackData(data);

      if (data.points.length > 0) {
        setCurrentIndex(0);
        setIsPlaying(false);

        const firstPoint = data.points[0];
        console.log('🎯 Starting playback at:', new Date(firstPoint.timestamp).toLocaleString());
        
        mapRef.current?.flyTo({
          center: [firstPoint.longitude, firstPoint.latitude],
          zoom: 14,
          duration: 1500
        });

        // Set full route
        const allCoords = data.points.map((p: PlaybackPoint) => [p.longitude, p.latitude]);
        
        const routeSource = mapRef.current?.getSource('route-line') as mapboxgl.GeoJSONSource;
        if (routeSource) {
          routeSource.setData({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: allCoords
            }
          });
          console.log('✅ Route set with', allCoords.length, 'points');
        }

        // Clear trail
        const trailSource = mapRef.current?.getSource('trail-line') as mapboxgl.GeoJSONSource;
        if (trailSource) {
          trailSource.setData({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: []
            }
          });
          console.log('✅ Trail cleared');
        }

        // Create car marker
        if (carMarkerRef.current) {
          carMarkerRef.current.remove();
        }

        const markerEl = createCarMarkerElement(
          firstPoint.speed,
          firstPoint.heading,
          firstPoint.ignition
        );

        carMarkerRef.current = new mapboxgl.Marker({ element: markerEl, anchor: 'center' })
          .setLngLat([firstPoint.longitude, firstPoint.latitude])
          .addTo(mapRef.current!);

      } else {
        setError('No GPS data found for this time range');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load playback data');
    } finally {
      setLoading(false);
    }
  };

  // Update trail and car position
  useEffect(() => {
    if (!playbackData || !mapRef.current) return;

    const currentPoint = playbackData.points[currentIndex];
    if (!currentPoint) return;

    // DEBUG: Log current point timestamp
    console.log(`🕐 Point ${currentIndex + 1}/${playbackData.points.length}: ${new Date(currentPoint.timestamp).toLocaleString()}`);

    // Update car position
    if (carMarkerRef.current) {
      carMarkerRef.current.setLngLat([currentPoint.longitude, currentPoint.latitude]);
      
      const newEl = createCarMarkerElement(
        currentPoint.speed,
        currentPoint.heading,
        currentPoint.ignition
      );
      
      carMarkerRef.current.getElement().innerHTML = newEl.innerHTML;

      mapRef.current?.easeTo({
        center: [currentPoint.longitude, currentPoint.latitude],
        duration: 500
      });
    }

    // Update trail to show ALL traveled points
    const traveledCoords = playbackData.points
      .slice(0, currentIndex + 1)
      .map((p: PlaybackPoint) => [p.longitude, p.latitude]);

    const trailSource = mapRef.current?.getSource('trail-line') as mapboxgl.GeoJSONSource;
    if (trailSource && traveledCoords.length >= 2) {
      trailSource.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: traveledCoords
        }
      });
    }

  }, [currentIndex, playbackData, createCarMarkerElement]);

  const startPlayback = () => {
    if (!playbackData || playbackData.points.length === 0) return;
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= playbackData.points.length - 1) {
          stopPlayback();
          return prev;
        }
        return prev + 1;
      });
    }, 1000 / playbackSpeed);
  };

  const pausePlayback = () => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const stopPlayback = () => {
    pausePlayback();
    setCurrentIndex(0);
  };

  const handleSliderChange = (event: Event, value: number | number[]) => {
    setCurrentIndex(value as number);
  };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);
    if (isPlaying) {
      pausePlayback();
      setTimeout(() => startPlayback(), 100);
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const currentPoint = playbackData?.points[currentIndex];

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Format timestamp with explicit timezone handling
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    return `${dateStr}, ${timeStr}`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        p: 3,
        borderRadius: 3,
        color: 'white',
        boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TimelineIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Trip Playback
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Replay and analyze vehicle journeys
            </Typography>
          </Box>
        </Box>
        
        <Tooltip title={`Switch to ${mapStyle === 'dark' ? 'light' : 'dark'} mode`}>
          <IconButton onClick={toggleMapStyle} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}>
            {mapStyle === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Input Controls */}
      <Card sx={{ mb: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* ✅ NEW: Vehicle Autocomplete */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={vehicles}
                loading={vehiclesLoading}
                value={selectedVehicle}
                onChange={(event, newValue) => {
                  setSelectedVehicle(newValue);
                  if (newValue?.imei) {
                    console.log('✅ Selected vehicle:', newValue.plateNumber, 'IMEI:', newValue.imei);
                    setImei(newValue.imei);
                    setError(''); // Clear any previous errors
                  }
                }}
                getOptionLabel={(vehicle) => 
                  `${vehicle.plateNumber} - ${vehicle.make} ${vehicle.model}`
                }
                renderOption={(props, vehicle) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {vehicle.plateNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {vehicle.make} {vehicle.model} ({vehicle.year})
                      </Typography>
                      {vehicle.imei && (
                        <Typography variant="caption" color="primary">
                          IMEI: {vehicle.imei}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Vehicle"
                    placeholder="Search by plate, make, or model"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={vehiclesLoading ? "Loading vehicles..." : "No vehicles with devices found"}
              />
            </Grid>

            {/* ✅ KEEP: Manual IMEI input as fallback */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Device IMEI (Manual)"
                value={imei}
                onChange={(e) => setImei(e.target.value)}
                placeholder="Or enter IMEI manually"
                InputProps={{
                  startAdornment: <DirectionsCar sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                helperText="Auto-filled when you select a vehicle"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Start Time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <CalendarMonth sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="datetime-local"
                label="End Time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <AccessTime sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={loadPlaybackData}
                disabled={loading}
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RouteIcon />}
                sx={{ height: 56, fontWeight: 'bold', boxShadow: 3 }}
              >
                {loading ? 'Loading...' : 'Load Trip'}
              </Button>
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {/* Trip Stats */}
      {playbackData && (
        <Card sx={{ mb: 2, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <DirectionsCar color="primary" sx={{ mr: 1 }} />
              Trip Overview
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={2}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">Vehicle</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">{playbackData.device.plateNumber}</Typography>
                  <Typography variant="caption" color="text.secondary">{playbackData.device.make} {playbackData.device.modelYear}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">Duration</Typography>
                  <Typography variant="h5" fontWeight="bold" color="info.main">{formatDuration(playbackData.stats.duration)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">Distance</Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">{playbackData.stats.distance} km</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">Max Speed</Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">{playbackData.stats.maxSpeed} km/h</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">Avg Speed</Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">{playbackData.stats.avgSpeed} km/h</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">Data Points</Typography>
                  <Typography variant="h5" fontWeight="bold" color="secondary.main">{playbackData.stats.pointCount}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Playback Controls */}
      {playbackData && playbackData.points.length > 0 && (
        <Paper sx={{ p: 3, mb: 2, boxShadow: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <IconButton
                  onClick={isPlaying ? pausePlayback : startPlayback}
                  disabled={currentIndex >= playbackData.points.length - 1}
                  size="large"
                  sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
                <IconButton onClick={stopPlayback} size="large" sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                  <Stop />
                </IconButton>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">Playback Speed</Typography>
                  <ToggleButtonGroup value={playbackSpeed} exclusive onChange={(e, val) => val && handleSpeedChange(val)} size="small">
                    <ToggleButton value={1}>x1</ToggleButton>
                    <ToggleButton value={5}>x5</ToggleButton>
                    <ToggleButton value={10}>x10</ToggleButton>
                    <ToggleButton value={25}>x25</ToggleButton>
                    <ToggleButton value={50}>x50</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {currentPoint && (
                  <>
                    <Chip icon={<SpeedIcon />} label={`${Math.round(currentPoint.speed)} km/h`} 
                      color={currentPoint.speed > 80 ? 'error' : currentPoint.speed > 60 ? 'warning' : 'success'} 
                      sx={{ fontWeight: 'bold' }} />
                    <Chip icon={<DirectionsCar />} label={currentPoint.ignition ? 'ENGINE ON' : 'ENGINE OFF'} 
                      color={currentPoint.ignition ? 'success' : 'default'} sx={{ fontWeight: 'bold' }} />
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  {currentPoint && formatTimestamp(currentPoint.timestamp)}
                </Typography>
                <Typography variant="caption" color="primary" fontWeight="bold">
                  Point {currentIndex + 1} of {playbackData.points.length}
                </Typography>
              </Box>
              <Slider
                value={currentIndex}
                onChange={handleSliderChange}
                min={0}
                max={playbackData.points.length - 1}
                sx={{
                  '& .MuiSlider-thumb': { width: 20, height: 20 },
                  '& .MuiSlider-track': { height: 6 },
                  '& .MuiSlider-rail': { height: 6, opacity: 0.3 }
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Map */}
      <Card sx={{ boxShadow: 6, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ height: '650px', width: '100%', position: 'relative' }}>
          <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
          {playbackData && (
            <Paper elevation={4} sx={{
              position: 'absolute', bottom: 20, left: 20, p: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(10px)', borderRadius: 2
            }}>
              <Typography variant="caption" fontWeight="bold">Map Legend</Typography>
              <Stack spacing={1} mt={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 24, height: 6, bgcolor: '#00ff00', borderRadius: 1, boxShadow: '0 0 10px #00ff00' }} />
                  <Typography variant="caption" fontWeight="600">Traveled Path</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 24, height: 4, bgcolor: '#888888', borderRadius: 1, opacity: 0.3 }} />
                  <Typography variant="caption" fontWeight="600">Full Route</Typography>
                </Box>
              </Stack>
            </Paper>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default TripPlaybackPage;